import { Dir } from '@angular/cdk/bidi';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { CurrencyPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, model, NgZone, OnDestroy, output, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/list';
import { MatTooltip } from '@angular/material/tooltip';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { NavigationDirection } from '@common/enums/navigation-direction';
import { PushPipe } from '@ngrx/component';
import { environment } from '@src/environments/environment';
import { AppService } from '@src/suite/app.service';
import { FieldDefValidations } from '@src/suite/common/enums/enums';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { StFullScreenComponent } from '@src/suite/common/st-full-screen-dialog/full-screen/full-screen';
import { selectEnforceRequiredFieldsOnCreate } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { selectViewStateSearchString } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { AssignmentRecordActions, RecordActions, RecordHttpActions } from '@src/suite/common/store/records/records.actions';
import { toLuxonRelativeDateString } from '@src/suite/common/utils/date-utils';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';
import { calculateRequiredFieldGroups, getAssignmentSecurityPrivsWrite, getCostPrivsRead, getCostPrivsWrite, getTypeInternal } from '@src/suite/common/utils/record-utils';
import { PlaybookService } from '@src/suite/playbook/services/playbook-service';
import { DateTime } from 'luxon';
import { fromEvent, Subject, takeUntil, tap } from 'rxjs';

import { ExternalActionsOverlayComponent } from '../../../playbook/components/external-actions-overlay/external-actions-overlay';
import { AttachmentsListComponent } from '../../components/attachments-list/attachments-list';
import { BetaComponent } from '../../components/beta/st-beta.component';
import { AddressFormComponent } from '../../controls/address-form/address-form';
import { AssignmentTypeComponent } from '../../controls/assignment-type/assignment-type';
import { CheckBoxComponent } from '../../controls/check-box/check-box';
import { CheckListComponent } from '../../controls/check-list/check-list';
import { ComboBoxComponent } from '../../controls/combo-box/combo-box';
import { DatePickerComponent } from '../../controls/date-picker/date-picker';
import { DateTimePickerComponent } from '../../controls/date-time-picker/date-time-picker';
import { DateTimeRangeComponent } from '../../controls/date-time-range/date-time-range';
import { DeskComponent } from '../../controls/desk/desk';
import { SpecialComponent } from '../../controls/special/special';
import { SpecialCostComponent } from '../../controls/special/special-cost/special-cost';
import { StTextAreaComponent } from '../../controls/st-text-area/st-text-area';
import { StTextAreaOverlayComponent } from '../../controls/st-text-area/text-area-overlay/st-text-area-overlay';
import { StaffIDListComponent } from '../../controls/staff-id-list/staff-id-list';
import { StaffIDListMultipleComponent } from '../../controls/staff-id-list/staff-id-list-multiple';
import { StatusComponent } from '../../controls/status/status';
import { TagsAutocompleteComponent } from '../../controls/tags/tags-autocomplete';
import { TextFieldComponent } from '../../controls/text-field/text-field';
import { TreeComponent } from '../../controls/tree/tree';
import { ClickStopPropagationDirective } from '../../directives/stop-propagation';

@Component({
    selector: 'st-assignment-full-screen',
    imports: [
        AddressFormComponent,
        AssignmentTypeComponent,
        AttachmentsListComponent,
        BetaComponent,
        CdkOverlayOrigin,
        CheckBoxComponent,
        CheckListComponent,
        ClickStopPropagationDirective,
        ComboBoxComponent,
        CurrencyPipe,
        DatePickerComponent,
        DateTimePickerComponent,
        DateTimeRangeComponent,
        DeskComponent,
        Dir,
        ExternalActionsOverlayComponent,
        MatAccordion,
        MatDivider,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        MatExpansionPanelTitle,
        MatIcon,
        MatIconButton,
        MatTooltip,
        PushPipe,
        SpecialComponent,
        SpecialCostComponent,
        StaffIDListComponent,
        StaffIDListMultipleComponent,
        StatusComponent,
        StTextAreaComponent,
        StTextAreaOverlayComponent,
        TagsAutocompleteComponent,
        TextFieldComponent,
        TreeComponent,
    ],
    templateUrl: './assignment-full-screen.html',
    styleUrl: './assignment-full-screen.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-assignment-full-screen',
    },
})
export class AssignmentFullScreenDialogComponent extends StFullScreenComponent implements AfterViewInit, OnDestroy {
    nomWebServicesJson = inject(NomWebServicesJson);
    appService = inject(AppService);
    ngZone = inject(NgZone);
    playbookService = inject(PlaybookService);

    pstFieldNames = PSTFieldNames;
    NavigationDirection = NavigationDirection;
    fieldDefValidations = FieldDefValidations;
    destroyed$ = new Subject<void>();
    environment = environment;

    readonly showMatDivider = signal(false);
    override readonly expandedGroup = signal<string>('ASSIGNMENT');
    override readonly data = model<StorytellingAssignmentRecord>();
    searchString$ = this.store.select(selectViewStateSearchString);

    enforceRequiredFieldsOnCreate = this.store.selectSignal(selectEnforceRequiredFieldsOnCreate);

    readonly scrollableDiv = viewChild<ElementRef>('scrollableDiv');

    readonly disabled = input(false, {
        transform: coerceBooleanProperty,
    });
    readonly parentTopicMosActive = input(false, {
        transform: coerceBooleanProperty,
    });
    readonly closeEmitter = output<void>();

    readonly assignmentEditable = computed(() => {
        return getAssignmentSecurityPrivsWrite(this.data()) && !this.data()?.Path?.isWasteBinPath && !this.disabled();
    });

    readonly assignmentDisabled = computed(() => {
        return !this.assignmentEditable();
    });

    readonly costEditable = computed(() => {
        return this.assignmentEditable() && getCostPrivsWrite(this.data());
    });

    readonly costDisabled = computed(() => {
        return !this.assignmentEditable() || !(this.appService.nomWebServicesJson?.HelloResults?.Supervisor || this.costEditable());
    });

    readonly costReadable = computed(() => {
        return getCostPrivsRead(this.data());
    });

    readonly costVisible = computed(() => {
        return this.nomWebServicesJson?.HelloResults?.Supervisor || this.costReadable();
    });

    readonly requiredFieldGroups = computed(() => {
        return calculateRequiredFieldGroups(this.data(), this.playbookService);
    });

    override readonly getType = computed(() => {
        return getTypeInternal(this.data()?.Type);
    });

    readonly updatedBy = computed(() => {
        const modBy = this.globalTablesService.Staff[this?.data()?.ModBy]?.DisplayName ? this.globalTablesService.Staff[this?.data()?.ModBy]?.DisplayName : this.data()?.ModBy;
        return `${this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebChangedByLabel))()} ${modBy}`;
    });

    readonly timeUpdatedLabel = computed(() => {
        return `${this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebChangedAgoLabel))()} ${toLuxonRelativeDateString(this.data()?.ModTime, this.appService.updatedAgoSignal())}`;
    });

    readonly showAttachments = computed(() => {
        return this.appService.attachmentsAllowed && !!this.data()?.Guid && environment?.demoFlag;
    });

    readonly specialProps = computed(() => {
        return this.globalTablesService.getSpecialPropertiesFor(this.getType()).filter(field => field.ID !== PSTFieldNames.PSTExternalFiles && field.ID !== PSTFieldNames.Relationships);
    });

    readonly isRTL = computed(() => {
        return this.globalTablesService.getRtl();
    });

    ngAfterViewInit(): void {
        this.ngZone.runOutsideAngular(() => {
            fromEvent(this.scrollableDiv().nativeElement, 'scroll')
                .pipe(
                    tap((ev: Event) => {
                        if ((ev.target as HTMLElement).scrollTop > 0) {
                            this.showMatDivider.set(true);
                        } else {
                            this.showMatDivider.set(false);
                        }
                    }),
                    takeUntil(this.destroyed$)
                )
                .subscribe();
        });
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    updateAssignment(value: any, fieldId: string) {
        const fieldDef = [{ value, key: fieldId }];
        // TODO: not cool to have this title logic here
        if (fieldId === PSTFieldNames.Title) {
            if (!value) return;
            this.data().Title = value.trim();
        }
        if (fieldId === PSTFieldNames.PSTTopicEventStartDate) {
            const castedVal: DateTime = value;
            if (castedVal > this.data()?.EventEnd) {
                fieldDef.push({ value: castedVal, key: PSTFieldNames.PSTTopicEventEndDate });
            }
        }
        this.store.dispatch(RecordHttpActions.saveRecordChanges({ record: this.data(), fieldsToUpdate: fieldDef }));
    }

    addObjectPlaceholder(anchorElement: MatIconButton) {
        if (!anchorElement) {
            return;
        }
        this.store.dispatch(RecordActions.createObjectPlaceholder({ record: this.data() }));
    }

    timezoneChanged(fieldId: string, event: any) {
        this.data.update(val => {
            val[fieldId] = event;
            return val;
        });
        this.store.dispatch(AssignmentRecordActions.updateAssignments({ records: [this.data()] }));
    }

    public isGroupSingleValue(property: string): boolean {
        return this.globalTablesService.isGroupSingleValue && (property === PSTFieldNames.PSTDesk || property === PSTFieldNames.PSTDesk);
    }

    public getList(group: string): FieldDef[] {
        return this.globalTablesService.getDynamicPropertiesFor(this.getType(), group).filter(p => p.ID !== PSTFieldNames.PSTTeamLocation);
    }
}
